import React, { useEffect, useState } from "react";
import { Box, Button, Grid, IconButton, Tooltip, Stack } from "@mui/material";
import FlexBetween from "../../components/FlexBetween";
import { useToast } from "../../services/utils";
import DataTable from "../../components/DataGrids/DataGrid";
import { GridColDef } from "@mui/x-data-grid";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ButtonComponent from "../../components/Button.Component";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import AddSharpIcon from "@mui/icons-material/AddSharp";
import {
  deleteConnectionKey,
  reactivateConnectionKey,
} from "../../services/connectionKeysService";
import { getFormattedDateTime } from "../../services/utils";
import DropdownMenu from "../../components/Molecules/DropdownMenu/DropdownMenu";
import Loader from "../../components/Molecules/Loader/Loader";
import {
  IconAudit,
  IconDeleteBlueSvg,
  IconCheckCircleIconGreenSvg,
  IconCrossCircleIconSvg,
  IconEyeBase,
  IconEditBase,
  IconReactivateSvg,
} from "../../common/utils/icons";
import { paginatedResponseFormat } from "../../services/constants";
import useFetchPaginatedAllConnectionKeys from "../../hooks/useFetchPaginatedAllConnectionKeys";
import { enableRowGroupByColumnProps } from "../../components/DataGridProps/DataGridProps";
import LimitChractersWithTooltip from "../../common/utils/LimitChractersWithTooltip";
import ConfirmDailog from "../../components/Dialogs/ConfirmDailog";
import { useAuth } from "../../contexts/AuthContext";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
import SearchFilterBar from "../../components/Molecules/FilterSearch/SearchFilterBar";
import { useAppTranslation } from "../../hooks/useAppTranslation";
import { getConfig } from "../../config/runtimeConfig";

// Centralized Filter Configuration for Connection Keys List
const CONNECTION_KEYS_FILTER_CONFIG = {
  // Default filter state - single source of truth
  DEFAULT_STATE: {
    query_name: "",
    status_filter: "Active", // Default to Active status
  },

  // Fields to persist in URL - extracted from default state keys
  get URL_FIELDS() {
    return Object.keys(this.DEFAULT_STATE);
  },

  // Field type mappings for URL serialization/deserialization
  FIELD_TYPES: {
    ID_FIELDS: [],
    STRING_FIELDS: ["query_name", "status_filter"],
    DATE_FIELDS: [],
  },
} as const;

const ConnectionKeys: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const { t } = useAppTranslation();

  // Permission checks for connection key actions
  const canEditConnectionKey = hasPermission("connection_key", "edit");
  const canViewConnectionKey = hasPermission("connection_key", "view");
  const canAddConnectionKey = hasPermission("connection_key", "add");
  const { rowsPerPage } = useRowsPerPage();
  // states
  const defaultPage = getConfig("REACT_APP_DEFAULT_PAGE", "1");
  const [page, setPage] = useState<number>(
    defaultPage ? parseInt(defaultPage) : 1
  );
  const [pSize, setPSize] = useState<number>(rowsPerPage);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isDownloadLoading, setIsDownloadLoading] = useState<boolean>(false);
  const [fileData, setFileData] = useState<any>(paginatedResponseFormat);
  const [connectionKeysData, setConnectionKeysData] = useState<any>(
    paginatedResponseFormat
  );

  // SearchFilterBar state - replaces old searchQuery state
  const [searchFilterData, setSearchFilterData] = useState<any>(() => {
    const initialState: any = {
      ...CONNECTION_KEYS_FILTER_CONFIG.DEFAULT_STATE,
    };

    // Restore filters from URL parameters
    CONNECTION_KEYS_FILTER_CONFIG.URL_FIELDS.forEach((field: string) => {
      const urlValue = searchParams.get(field);
      if (urlValue) {
        if (
          CONNECTION_KEYS_FILTER_CONFIG.FIELD_TYPES.ID_FIELDS.includes(
            field as never
          )
        ) {
          initialState[field] = parseInt(urlValue);
        } else {
          initialState[field] = urlValue;
        }
      }
    });

    return initialState;
  });

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [connectionKeyToDelete, setConnectionKeyToDelete] = useState<any>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // hooks
  const [fetchedConnectionKeys, refetchConnectionKeys] =
    useFetchPaginatedAllConnectionKeys({
      setIsLoading,
      page,
      pSize,
      searchFilterData,
    });

  // Sync filters to URL parameters (state → URL)
  useEffect(() => {
    const newParams = new URLSearchParams();

    CONNECTION_KEYS_FILTER_CONFIG.URL_FIELDS.forEach((field) => {
      const value = searchFilterData[field];
      if (value !== null && value !== undefined && value !== "") {
        // Don't add default values to URL to keep it clean
        if (field === "status_filter" && value === "Active") {
          return; // Skip default status filter
        }
        newParams.set(field, String(value));
      }
    });

    // Only update URL if params have changed
    const currentParamsString = searchParams.toString();
    const newParamsString = newParams.toString();
    if (currentParamsString !== newParamsString) {
      setSearchParams(newParams, { replace: true });
    }
  }, [searchFilterData, searchParams, setSearchParams]);

  // Update filter state when URL changes (URL → state for back/forward navigation)
  useEffect(() => {
    const urlFilterState: any = {
      ...CONNECTION_KEYS_FILTER_CONFIG.DEFAULT_STATE,
    };

    // Extract filter parameters from URL
    CONNECTION_KEYS_FILTER_CONFIG.URL_FIELDS.forEach((field: string) => {
      const urlValue = searchParams.get(field);
      if (urlValue) {
        if (
          CONNECTION_KEYS_FILTER_CONFIG.FIELD_TYPES.ID_FIELDS.includes(
            field as never
          )
        ) {
          urlFilterState[field] = parseInt(urlValue);
        } else {
          urlFilterState[field] = urlValue;
        }
      }
    });

    // Check if URL filter state differs from current filter state
    const hasFilterChanges = Object.keys(urlFilterState).some((key) => {
      const urlValue = urlFilterState[key];
      const currentValue = searchFilterData[key];

      // Handle null/undefined/empty string comparisons
      const normalizedUrlValue =
        urlValue === null || urlValue === undefined || urlValue === ""
          ? ""
          : urlValue;
      const normalizedCurrentValue =
        currentValue === null ||
        currentValue === undefined ||
        currentValue === ""
          ? ""
          : currentValue;

      return normalizedUrlValue !== normalizedCurrentValue;
    });

    // Only update state if there are actual changes to prevent infinite loops
    if (hasFilterChanges) {
      setSearchFilterData(urlFilterState);
    }
  }, [searchParams]); // Only depend on searchParams to detect URL changes

  // Reset page to 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [searchFilterData]);

  const handelClickEvent = () => {
    navigate("/connection-keys/add");
  };

  // Handle delete connection key
  const handleDeleteConnectionKey = (connectionKey: any) => {
    setConnectionKeyToDelete(connectionKey);
    setDeleteConfirmOpen(true);
  };

  // Confirm delete action
  const confirmDelete = async () => {
    if (!connectionKeyToDelete) return;

    try {
      setDeleteLoading(true);
      const result = await deleteConnectionKey(connectionKeyToDelete.id);
      if (result && result.status === 200) {
        showToast(
          t("connectionKeys.toast.deleted", {
            name: connectionKeyToDelete.name,
          }),
          "success"
        );
        setDeleteConfirmOpen(false);
        setConnectionKeyToDelete(null);
        // Update the connection keys list to mark as inactive
        setFileData((prev: any) => ({
          ...prev,
          items: prev?.items?.map((item: any) =>
            item.id === connectionKeyToDelete.id
              ? { ...item, is_active: false }
              : item
          ),
        }));
      }
    } catch (error: any) {
      showToast(
        error?.message || t("connectionKeys.toast.failedDelete"),
        "error"
      );
    } finally {
      setDeleteLoading(false);
      setDeleteConfirmOpen(false);
    }
  };

  // Handle reactivate connection key
  const handleReactivate = async (id: number, connectionKeyName: string) => {
    setIsLoading(true);
    try {
      await reactivateConnectionKey(id);
      showToast(
        t("connectionKeys.toast.reactivated", { name: connectionKeyName }),
        "success"
      );

      // Optimistic UI update - immediately update the is_active flag in local state
      setFileData((prev: any) => ({
        ...prev,
        items: prev?.items?.map((item: any) =>
          item.id === id ? { ...item, is_active: true } : item
        ),
      }));

      // Refetch the list to update the UI with fresh data from database
      refetchConnectionKeys();
    } catch (error) {
      showToast(t("connectionKeys.toast.failedReactivate"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  const columns: GridColDef[] = [
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      width: 1,
      minWidth: 20,
      flex: 0,
      cellClassName: "no-padding-cell",
      headerClassName: "no-padding-cell",
      renderCell: (params: any) => <></>,
    },
    {
      field: "id",
      headerName: t("connectionKeys.list.columns.id"),
      minWidth: 80,
      flex: 1,
      renderCell: (params: any) => {
        return <span>{params.id}</span>;
      },
    },
    {
      field: "name",
      headerName: t("connectionKeys.list.columns.name"),
      minWidth: 140,
      flex: 1,
      renderCell: (params: any) => {
        return <LimitChractersWithTooltip value={params?.value} />;
      },
    },
    {
      field: "modified_by_user",
      headerName: t("connectionKeys.list.columns.modifiedBy"),
      minWidth: 140,
      flex: 1,
      renderCell: (params: any) => {
        return <LimitChractersWithTooltip value={params?.value?.username} />;
      },
    },
    {
      field: "updated_on",
      headerName: t("connectionKeys.list.columns.updatedOn"),
      minWidth: 180,
      flex: 1,
      renderCell: (params: any) => {
        return getFormattedDateTime(params.value);
      },
    },
    {
      field: "is_active",
      headerName: t("connectionKeys.list.columns.status"),
      minWidth: 180,
      flex: 1,
      renderCell: (params: any) => {
        return params.value ? (
          <Box title={t("connectionKeys.list.tooltip.active")}>
            <IconCheckCircleIconGreenSvg width={21} height={21} />
          </Box>
        ) : (
          <Box title={t("connectionKeys.list.tooltip.inactive")}>
            <IconCrossCircleIconSvg width={21} height={21} />
          </Box>
        );
      },
    },
    {
      field: "action",
      headerName: t("connectionKeys.list.columns.action"),
      align: "right",
      headerAlign: "right",
      width: 130,
      renderCell: (params: any) => {
        const handleDelete = () => {
          handleDeleteConnectionKey(params.row);
        };
        const isActive = params?.row?.is_active === true;

        return (
          <>
            {/* For active records, show all action buttons */}
            {isActive && (
              <>
                {canViewConnectionKey && (
                  <Tooltip
                    title={t("connectionKeys.list.tooltip.view")}
                    placement="top"
                    arrow
                  >
                    <IconButton
                      className="datagrid-action-btn"
                      color="inherit"
                      onClick={() => {
                        navigate(`/connection-keys/view/${params.row.id}`);
                      }}
                    >
                      <IconEyeBase />
                    </IconButton>
                  </Tooltip>
                )}
                {canEditConnectionKey && (
                  <Tooltip
                    title={t("connectionKeys.list.tooltip.edit")}
                    placement="top"
                    arrow
                  >
                    <IconButton
                      className="datagrid-action-btn"
                      color="inherit"
                      onClick={() => {
                        navigate(`/connection-keys/edit/${params.row.id}`);
                      }}
                    >
                      <IconEditBase />
                    </IconButton>
                  </Tooltip>
                )}
                <DropdownMenu
                  dropdownMenuItems={[
                    <IconButton
                      key="audit-connectionKeys"
                      className="datagrid-action-btn"
                      color="inherit"
                      onClick={() =>
                        navigate(
                          `/connection-keys/auditConnectionKey/${params.row.id}`,
                          {
                            state: {
                              details: {
                                name: params.row.name,
                                id: params.row.id,
                              },
                            },
                          }
                        )
                      }
                    >
                      <IconAudit /> {t("connectionKeys.list.dropdown.audit")}
                    </IconButton>,
                    // Delete Connection Key - requires edit permission
                    ...(canEditConnectionKey
                      ? [
                          <IconButton
                            key="delete-connection-key"
                            className="datagrid-action-btn"
                            color="inherit"
                            onClick={() => handleDelete()}
                          >
                            <IconDeleteBlueSvg />
                            {t("connectionKeys.list.dropdown.delete")}
                          </IconButton>,
                        ]
                      : []),
                  ]}
                />
              </>
            )}

            {/* For inactive records, show ONLY Reactivate button */}
            {!isActive && canEditConnectionKey && (
              <Tooltip
                title={t("connectionKeys.list.tooltip.reactivate")}
                placement="top"
                arrow
              >
                <IconButton
                  className="datagrid-action-btn"
                  color="inherit"
                  onClick={() =>
                    handleReactivate(params.row.id, params.row.name)
                  }
                >
                  <IconReactivateSvg />
                </IconButton>
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];

  // for update connection keys list data
  useEffect(() => {
    if (fetchedConnectionKeys?.items) {
      setFileData(fetchedConnectionKeys);
      setConnectionKeysData(fetchedConnectionKeys);
    }
  }, [fetchedConnectionKeys]);

  return (
    <Box>
      {(isLoading || isDownloadLoading) && (
        <Loader isLoading={isLoading || isDownloadLoading} />
      )}
      {deleteLoading && <Loader isLoading={deleteLoading} zIndex={1400} />}
      <ConfirmDailog
        isDailogOpen={deleteConfirmOpen}
        handleCancelAction={() => setDeleteConfirmOpen(false)}
        handleConfirmAction={confirmDelete}
        dailogTitle={t("connectionKeys.dialog.deleteTitle")}
        dailogDescription={t("connectionKeys.dialog.deleteDescription", {
          name: connectionKeyToDelete?.name,
        })}
      />
      <Box className="text-box-card list-page-card compact-text-box-card">
        <Grid container rowSpacing={2.5} columnSpacing={2}>
          <Grid item xs>
            <SearchFilterBar
              largeWidth={true}
              searchFilterData={searchFilterData}
              setSearchFilterData={setSearchFilterData}
              requiredFields={["query_name", "status_filter"]}
              primaryFields={["query_name", "status_filter"]}
              fieldRequirements={{
                queryNamePlaceholder: t("connectionKeys.list.placeholder"),
                defaultStatusFilter: "Active",
              }}
            />
          </Grid>
          {canAddConnectionKey && (
            <Grid item display={"flex"} justifyContent={"flex-end"}>
              <Stack>
                <Box sx={{ display: "flex", columnGap: 2 }}>
                  <ButtonComponent
                    handelClickEvent={handelClickEvent}
                    className="btn-orange"
                  >
                    <AddSharpIcon sx={{ marginRight: "4px" }} />{" "}
                    {t("connectionKeys.list.title")}
                  </ButtonComponent>
                </Box>
              </Stack>
            </Grid>
          )}
        </Grid>
      </Box>

      <FlexBetween>
        <DataTable
          dataColumns={columns || []}
          dataRows={fileData?.items || []}
          loading={isLoading}
          dataListTitle={t("connectionKeys.list.dataListTitle")}
          className="dataTable no-radius hide-progress-icon"
          paginationMode="server"
          rowCount={fileData?.total || 0}
          rowGroupByColumnProps={enableRowGroupByColumnProps}
          paginationModel={{
            page: page - 1,
            pageSize: pSize,
          }}
          onPaginationModelChange={(params: any) => {
            if (params.pageSize !== pSize || params.page !== page - 1) {
              setPage(params.page + 1);
              setPSize(params.pageSize);
            }
          }}
          singlePageMaxHeightDiff={360}
        />
      </FlexBetween>
    </Box>
  );
};

export default ConnectionKeys;
